import type { Evidence, Finding, FindingLayer, Flag, Severity } from '@fraudeai/schema';

const ATTRIBUTION_FLAGS = new Set<Flag>([
  'ANTIDETECT',
  'AUTOMATION',
  'BOT',
  'SPOOFING',
  'VIRTUAL_MACHINE',
]);

/**
 * Подпись к слою находки. Пользователь должен видеть разницу между «сервер
 * увидел это сам» и «браузер так сказал»: доверие к этим двум утверждениям
 * принципиально разное, и скрывать различие было бы нечестно.
 */
const LAYER_LABELS: Record<FindingLayer, { text: string; title: string }> = {
  network: {
    text: 'сетевой слой',
    title: 'Замечено в самом HTTP-запросе. Подделать из JavaScript нельзя.',
  },
  'browser-verified': {
    text: 'проверено сервером',
    title: 'Сервер пересчитал вывод заново по сырым измерениям, не доверяя выводам браузера.',
  },
  'browser-attested': {
    text: 'со слов браузера',
    title: 'Измерено в браузере. Сервер проверить это не может, поэтому вес находки снижен.',
  },
};

const SEVERITY_LABELS: Record<Severity, string> = {
  critical: 'критично',
  high: 'высокий',
  medium: 'средний',
  low: 'низкий',
  info: 'справочно',
};

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'var(--critical)',
  high: 'var(--high)',
  medium: 'var(--medium)',
  low: 'var(--low)',
  info: 'var(--info)',
};

function EvidenceRow({ item }: { item: Evidence }) {
  return (
    <div className="evidence-row">
      <dt>{item.label}</dt>
      <dd>
        {item.detail !== undefined && <span>{item.detail}</span>}
        {item.expected !== undefined && (
          <span className="expected">{String(item.expected)}</span>
        )}
        {item.expected !== undefined && item.actual !== undefined && (
          <span className="arrow">→</span>
        )}
        {item.actual !== undefined && <span className="actual">{String(item.actual)}</span>}
      </dd>
    </div>
  );
}

export function Findings({ findings }: { findings: Finding[] }) {
  // Теневые находки в публичный список не показываем: их правила ещё не
  // доказаны. Сервер их и не присылает, но клиентский предпросмотр может —
  // отсюда фильтр здесь, в единственном месте отрисовки.
  const visible = findings.filter((finding) => !finding.shadow);

  if (visible.length === 0) {
    return (
      <section>
        <h3>Что найдено</h3>
        <div className="all-clear">
          Ни одно правило не сработало. Слои окружения согласованы между собой: браузер,
          движок JavaScript, видеокарта, шрифты и системные голоса рассказывают одну
          и ту же историю.
        </div>
      </section>
    );
  }

  const groups = new Map<Flag, Set<string>>();
  for (const finding of visible) {
    if (
      finding.confidence < 0.5 ||
      (finding.evidenceTier !== 'direct' && finding.evidenceTier !== 'corroborating')
    ) {
      continue;
    }
    for (const flag of finding.flags) {
      if (!ATTRIBUTION_FLAGS.has(flag)) continue;
      const values = groups.get(flag) ?? new Set<string>();
      values.add(finding.evidenceGroup ?? finding.ruleId);
      groups.set(flag, values);
    }
  }

  const evidenceLabel = (finding: Finding): { text: string; title: string } | null => {
    if (finding.evidenceTier === 'direct') {
      return {
        text: 'прямое доказательство',
        title: 'Эта находка может подтверждать указанный факт без второй независимой группы.',
      };
    }
    if (finding.evidenceTier === 'contextual') {
      return {
        text: 'контекст',
        title: 'Наблюдение показано в отчёте, но не снижает подлинность браузера.',
      };
    }
    if (finding.evidenceTier !== 'corroborating') return null;

    const corroborated = finding.flags.some(
      (flag) => ATTRIBUTION_FLAGS.has(flag) && (groups.get(flag)?.size ?? 0) >= 2,
    );
    return corroborated
      ? {
          text: 'подтверждено связкой',
          title: 'Тот же вывод подтверждает другая независимая группа сигналов.',
        }
      : {
          text: 'нужно подтверждение',
          title: 'Одиночная косвенная улика видна, но сама не публикует обвинительный флаг.',
        };
  };

  return (
    <section>
      <h3>Что найдено — {visible.length}</h3>
      <p className="hint">
        Каждое срабатывание раскрывается: видно, какие именно значения не сошлись
        и почему это считается признаком подмены.
      </p>

      {visible.map((finding) => {
        const attribution = evidenceLabel(finding);
        return (
          <details
            className="finding"
            key={finding.ruleId}
            style={{ '--sev': SEVERITY_COLORS[finding.severity] } as React.CSSProperties}
            open={finding.severity === 'critical'}
          >
            <summary>
              <span className="sev-badge">{SEVERITY_LABELS[finding.severity]}</span>
              <span className="finding-title">{finding.title}</span>
              {attribution && (
                <span className="evidence-tier-badge" title={attribution.title}>
                  {attribution.text}
                </span>
              )}
              {finding.layer && (
                <span className="layer-badge" title={LAYER_LABELS[finding.layer].title}>
                  {LAYER_LABELS[finding.layer].text}
                </span>
              )}
            </summary>
            <div className="finding-body">
              <p>{finding.explanation}</p>
              {finding.evidence.length > 0 && (
                <dl className="evidence">
                  {finding.evidence.map((item, index) => (
                    <EvidenceRow key={`${finding.ruleId}-${index}`} item={item} />
                  ))}
                </dl>
              )}
              {finding.confidence < 1 && (
                <p style={{ marginTop: 12, marginBottom: 0, fontSize: 12.5, color: 'var(--text-faint)' }}>
                  Уверенность правила — {Math.round(finding.confidence * 100)}%. Штраф к скору
                  снижен с учётом силы и независимости доказательства.
                </p>
              )}
            </div>
          </details>
        );
      })}
    </section>
  );
}
